import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  keycloak,
  initKeycloak,
  getUserInfo,
  login,
  logout,
  hasRole,
  hasAnyRole,
  hasAllRoles,
} from '../services/keycloak';
import { iamApi, setCurrentTenantId } from '../services/api';
import { store } from '../store/store';
import { api as reduxApi } from '../store/api/baseApi';
import type { User } from '../types/auth';

export interface Organization {
  id: string;
  name: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  orgId: string | null;
  orgs: Organization[];
  switchOrg: (org: Organization) => void;
  login: () => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

let _didInit = false;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(localStorage.getItem('tenantId'));
  const [orgs, setOrgs] = useState<Organization[]>([]);

  const switchOrg = (org: Organization) => {
    setCurrentTenantId(org.id);
    setOrgId(org.id);
    // All RTK Query cache keys are tenant-agnostic (X-Tenant-Id is just a
    // header), so without this every page keeps showing the previous org's
    // cached data until an unrelated refetch happens to occur.
    store.dispatch(reduxApi.util.resetApiState());
  };

  useEffect(() => {
    if (_didInit) return;
    _didInit = true;

    initKeycloak()
      .then(async (auth) => {
        setIsAuthenticated(auth);
        if (auth) {
          setUser(getUserInfo());
          setToken(keycloak.token ?? null);

          // Fetch orgs from IAM
          try {
            const res = await iamApi.get<Organization[]>('/orgs/my');
            const fetchedOrgs = Array.isArray(res.data) ? res.data : [];
            setOrgs(fetchedOrgs);

            // Use persisted org or default to first
            const persisted = localStorage.getItem('tenantId');
            const valid = fetchedOrgs.find((o) => o.id === persisted);
            const selected = valid ?? fetchedOrgs[0] ?? null;
            if (selected) {
              setCurrentTenantId(selected.id);
              setOrgId(selected.id);
            }
          } catch {
            // IAM unavailable — fall back to env default
            const fallback = import.meta.env.VITE_ORG_ID ?? null;
            if (fallback) {
              setCurrentTenantId(fallback);
              setOrgId(fallback);
            }
          }
        }
      })
      .finally(() => setIsLoading(false));

    const interval = setInterval(() => {
      if (keycloak.authenticated) {
        keycloak.updateToken(30).then((refreshed) => {
          if (refreshed && keycloak.token) setToken(keycloak.token);
        }).catch(() => {
          setIsAuthenticated(false);
          setUser(null);
          setToken(null);
        });
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderColor: 'var(--accent)' }}
        />
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        token,
        orgId,
        orgs,
        switchOrg,
        login,
        logout,
        hasRole,
        hasAnyRole,
        hasAllRoles,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
